import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { toast } from "sonner";
import { Loader2, Plus, X, CheckCircle2 } from "lucide-react";

const SKILL_SUGGESTIONS = ["Prompting", "Automation", "No-code AI", "Data Basics", "Model Evaluation", "Python", "APIs", "Writing"];

export default function Profile() {
  const { checkAuth } = useAuth();
  const [form, setForm] = useState(null);
  const [skillInput, setSkillInput] = useState("");
  const [interestInput, setInterestInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/profile").then((r) => setForm({
      name: r.data.name || "", headline: r.data.headline || "", college: r.data.college || "",
      graduation_year: r.data.graduation_year || "", location: r.data.location || "", bio: r.data.bio || "",
      skills: r.data.skills || [], interests: r.data.interests || [],
      resume_link: r.data.resume_link || "", github: r.data.github || "", linkedin: r.data.linkedin || "",
      profile_complete: r.data.profile_complete, email: r.data.email,
    })).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addChip = (key, value, clear) => {
    const v = value.trim();
    if (!v || form[key].includes(v)) return;
    set(key, [...form[key], v]);
    clear("");
  };
  const removeChip = (key, value) => set(key, form[key].filter((x) => x !== value));

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setForm((f) => ({ ...f, profile_complete: data.profile_complete }));
      await checkAuth();
      toast.success(data.profile_complete ? "Profile saved — you're ready to apply!" : "Profile saved");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <PageShell><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7A222B]" /></div></PageShell>;

  const inputCls = "mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-[#2D2825] focus:outline-none focus:border-[#7A222B] focus:ring-2 focus:ring-[#7A222B]/10 transition";

  return (
    <PageShell>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">Profile</span>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>Your AI career profile</h1>
        </div>
        {form.profile_complete && (
          <span data-testid="profile-complete-badge" className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#E6F4EA] text-[#2E7D32]">
            <CheckCircle2 className="w-4 h-4" /> Ready to apply
          </span>
        )}
      </div>

      <form onSubmit={save} className="mt-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {error && <div className="bg-[#FAF0F1] border border-[#D99B9F] text-[#7A222B] text-sm rounded-xl px-4 py-3">{error}</div>}
          <div className="bg-white border border-[#EAE3DB] rounded-3xl p-7 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-[#2D2825]">Full name</label>
                <input className={inputCls} data-testid="profile-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2825]">Headline</label>
                <input className={inputCls} data-testid="profile-headline" placeholder="Aspiring Prompt Engineer" value={form.headline} onChange={(e) => set("headline", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2825]">College / Institute</label>
                <input className={inputCls} data-testid="profile-college" value={form.college} onChange={(e) => set("college", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2825]">Graduation year</label>
                <input className={inputCls} data-testid="profile-grad-year" placeholder="2027" value={form.graduation_year} onChange={(e) => set("graduation_year", e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D2825]">Location</label>
                <input className={inputCls} data-testid="profile-location" placeholder="Bengaluru" value={form.location} onChange={(e) => set("location", e.target.value)} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2825]">About you</label>
              <textarea className={inputCls} rows={3} data-testid="profile-bio" placeholder="A short intro about your AI interests…" value={form.bio} onChange={(e) => set("bio", e.target.value)} />
            </div>
          </div>

          <ChipEditor
            label="AI Skills (add at least 3)" testid="skills"
            chips={form.skills} input={skillInput} setInput={setSkillInput}
            onAdd={() => addChip("skills", skillInput, setSkillInput)} onRemove={(v) => removeChip("skills", v)}
            suggestions={SKILL_SUGGESTIONS.filter((s) => !form.skills.includes(s))} onSuggest={(s) => set("skills", [...form.skills, s])}
          />
          <ChipEditor
            label="Interests" testid="interests"
            chips={form.interests} input={interestInput} setInput={setInterestInput}
            onAdd={() => addChip("interests", interestInput, setInterestInput)} onRemove={(v) => removeChip("interests", v)}
          />
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#EAE3DB] rounded-3xl p-7 space-y-5">
            <h3 className="font-semibold text-[#2D2825]">Links</h3>
            <div>
              <label className="text-sm font-medium text-[#2D2825]">Resume link (optional)</label>
              <input className={inputCls} data-testid="profile-resume" placeholder="https://drive.google.com/…" value={form.resume_link} onChange={(e) => set("resume_link", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2825]">GitHub</label>
              <input className={inputCls} data-testid="profile-github" placeholder="github.com/you" value={form.github} onChange={(e) => set("github", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D2825]">LinkedIn</label>
              <input className={inputCls} data-testid="profile-linkedin" placeholder="linkedin.com/in/you" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="profile-save-btn" className="w-full flex items-center justify-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white font-semibold py-3.5 rounded-xl transition-colors disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save profile
          </button>
        </div>
      </form>
    </PageShell>
  );
}

function ChipEditor({ label, testid, chips, input, setInput, onAdd, onRemove, suggestions = [], onSuggest }) {
  return (
    <div className="bg-white border border-[#EAE3DB] rounded-3xl p-7">
      <label className="text-sm font-medium text-[#2D2825]">{label}</label>
      <div className="mt-3 flex gap-2">
        <input
          className="flex-1 rounded-xl border border-[#EAE3DB] bg-white px-4 py-2.5 text-[#2D2825] focus:outline-none focus:border-[#7A222B] transition"
          data-testid={`${testid}-input`} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          placeholder="Type and press Enter"
        />
        <button type="button" onClick={onAdd} data-testid={`${testid}-add-btn`} className="px-4 rounded-xl bg-[#F5EFE6] hover:bg-[#EAE3DB] text-[#7A222B] transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {chips.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c} data-testid={`${testid}-chip-${c}`} className="inline-flex items-center gap-1.5 bg-[#FDF0EC] text-[#7A222B] text-sm px-3 py-1.5 rounded-full">
              {c} <button type="button" onClick={() => onRemove(c)}><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button key={s} type="button" onClick={() => onSuggest(s)} className="text-xs text-[#6E665E] border border-dashed border-[#D6C9BA] px-3 py-1.5 rounded-full hover:border-[#7A222B] hover:text-[#7A222B] transition-colors">
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
